// ============================================================================
// GAME
// ============================================================================

import CONFIG from './config.js';
import { TileMap } from './tilemap.js';
import { Camera } from './camera.js';
import { Input } from './input.js';
import { Player } from './entities/Player.js';
import { Cupboard } from './entities/Cupboard.js';
import { DetachedDoor } from './entities/DetachedDoor.js';
import { AlarmClock } from './entities/AlarmClock.js';
import { DetachedClockHand } from './entities/DetachedClockHand.js';
import { Bed } from './entities/Bed.js';
import { SideTable } from './entities/SideTable.js';
import { Tree } from './entities/Tree.js';
import { Wardrobe } from './entities/Wardrobe.js';
import { Desk } from './entities/Desk.js';
import { Chair } from './entities/Chair.js';
import { Chest } from './entities/Chest.js';
import { DetachedMirrorPiece } from './entities/DetachedMirrorPiece.js';
import { BrokenClock } from './entities/BrokenClock.js';
import { Door } from './entities/Door.js';
import { ParticleSystem } from './systems/ParticleSystem.js';
import { AudioSystem } from './systems/AudioSystem.js';
import { PhysicsSystem } from './systems/PhysicsSystem.js';
import { TextureManager } from './systems/TextureManager.js';
import { TiledMapLoader } from './loaders/TiledMapLoader.js';

// Make Input available globally for Player class (which uses it directly)
window.Input = Input;

export class Game {
    /**
     * Load game from a Tiled map file
     * @param {string} mapPath - Path to the Tiled JSON map file
     * @returns {Promise<Game>} Game instance loaded from map
     */
    static async loadFromTiled(mapPath) {
        const game = new Game(true); // Skip initialization
        await game.loadTiledMap(mapPath);
        return game;
    }

    constructor(skipInit = false) {
        console.log('Game constructor called with skipInit =', skipInit);
        this.canvas = document.getElementById('gameCanvas');
        this.ctx = this.canvas.getContext('2d');
        
        // Intro screen state
        this.showIntro = true;
        
        // Restart button state
        this.restartButtonBounds = null;
        
        // Disable image smoothing for pixel-perfect rendering (prevents tile flickering)
        this.ctx.imageSmoothingEnabled = false;
        this.ctx.webkitImageSmoothingEnabled = false;
        this.ctx.mozImageSmoothingEnabled = false;
        this.ctx.msImageSmoothingEnabled = false;

        // Set canvas dimensions from config to ensure consistency
        this.canvas.width = CONFIG.canvasWidth;
        this.canvas.height = CONFIG.canvasHeight;
        
        // Status bar height (excluded from camera view)
        this.hudHeight = 60;
        
        // Debug tracking
        this.lastInteractionAggression = 0;  // Last aggression value from interaction/throw

        // Initialize arrays
        this.detachedDoors = [];
        this.detachedMirrorPieces = [];
        this.detachedClockHands = [];
        this.brokenClocks = [];
        this.entities = [];
        this.fragments = [];
        this.doors = [];
        this.trees = [];

        // Initialize TextureManager (needed for both initialization paths)
        this.textureManager = new TextureManager();

        // Track if we're loading from Tiled to prevent default entity creation
        this.loadingFromTiled = skipInit;
        
        // Initialize entity properties to null to ensure they're not accidentally used
        this.player = null;
        this.cupboard = null;
        this.cupboard2 = null;
        this.bed = null;
        this.wardrobe = null;
        this.alarmClock = null;
        this.desk = null;
        this.chair = null;
        this.chest = null;

        if (skipInit) {
            // Will be initialized by loadTiledMap - do NOT create default entities
            console.log('Game constructor: Skipping default initialization (will load from Tiled)');
            console.log('Game constructor: All entity properties initialized to null');
            return;
        }

        console.log('Game constructor: Creating default procedural map');
        this.tilemap = new TileMap(CONFIG.worldWidth, CONFIG.worldHeight, CONFIG.tileSize);
        const actualWorldWidth = this.tilemap.cols * this.tilemap.tileSize;
        const actualWorldHeight = this.tilemap.rows * this.tilemap.tileSize;
        
        // Camera view excludes the status bar height
        const cameraViewHeight = this.canvas.height - this.hudHeight;
        this.camera = new Camera(actualWorldWidth, actualWorldHeight, this.canvas.width, cameraViewHeight);
        this.physics = new PhysicsSystem(actualWorldWidth, actualWorldHeight);
        this.physics.initDebugRenderer(this.canvas, this.ctx);
        
        this.wallBodies = this.physics.createWallBodies(this.tilemap);

        const houseStartCol = Math.floor(this.tilemap.cols / 4);
        const houseHeight = Math.floor(this.tilemap.rows / 2);
        const houseStartRow = this.tilemap.rows - houseHeight - 2;
        const startX = (houseStartCol + 4) * CONFIG.tileSize + CONFIG.tileSize / 2;
        const startY = (houseStartRow + 4) * CONFIG.tileSize + CONFIG.tileSize / 2;

        this.player = new Player(startX, startY, this.physics);
        this.cupboard = new Cupboard(startX + 150, startY, this.physics);
        
        const bedX = startX - 100;
        const bedY = startY - 50;
        this.bed = new Bed(bedX, bedY, this.physics);
        
        const sideTableX = bedX + 90;
        const sideTableY = bedY;
        // AlarmClock now includes SideTable
        this.alarmClock = new AlarmClock(sideTableX, sideTableY, this.physics, null);
        
        const wardrobeX = bedX - 60;
        const wardrobeY = bedY + 100;
        this.wardrobe = new Wardrobe(wardrobeX, wardrobeY, this.physics);
        
        const houseWidth = Math.floor(this.tilemap.cols / 2) + 6;
        const midCol = houseStartCol + Math.floor(houseWidth / 2);
        const otherRoomCenterX = (midCol + houseStartCol + houseWidth) / 2 * CONFIG.tileSize;
        const otherRoomCenterY = (houseStartRow + houseStartRow + houseHeight) / 2 * CONFIG.tileSize;
        
        this.desk = new Desk(otherRoomCenterX + 100, otherRoomCenterY - 80, this.physics);
        this.chair = new Chair(otherRoomCenterX + 100, otherRoomCenterY - 20, this.physics);
        this.chest = new Chest(otherRoomCenterX - 80, otherRoomCenterY + 50, this.physics);
        
        const doorWidth = 40;
        const doorHeight = 50; // Same as tile height
        // All doors use hinged door mechanism
        // Top door: horizontal
        this.topDoor = new Door(midCol * CONFIG.tileSize + CONFIG.tileSize / 2, houseStartRow * CONFIG.tileSize, doorWidth, doorHeight, 'left', this.physics, 'horizontal');
        // Bottom door: horizontal
        this.bottomDoor = new Door(midCol * CONFIG.tileSize + CONFIG.tileSize / 2, (houseStartRow + houseHeight - 1) * CONFIG.tileSize, doorWidth, doorHeight, 'right', this.physics, 'horizontal');
        // Room door: vertical, one tile wide (centered)
        // For vertical doors: width = vertical dimension, height = horizontal dimension
        this.roomDoor = new Door(midCol * CONFIG.tileSize, (houseStartRow + Math.floor(houseHeight / 2)) * CONFIG.tileSize + CONFIG.tileSize / 2, doorHeight, CONFIG.tileSize, 'left', this.physics, 'vertical');
        this.doors = [this.topDoor, this.bottomDoor, this.roomDoor];
        
        const houseCenterX = (this.tilemap.cols / 2) * CONFIG.tileSize;
        const houseCenterY = (this.tilemap.rows / 2) * CONFIG.tileSize;
        this.trees = [
            new Tree(houseCenterX - 400, houseCenterY - 400, this.physics),
            new Tree(houseCenterX - 400, houseCenterY, this.physics),
            new Tree(houseCenterX, houseCenterY - 400, this.physics)
        ];

        this.particles = new ParticleSystem();
        this.audio = new AudioSystem();
        // Initialize audio (async, but don't await - let it load in background)
        this.audio.init().catch(e => console.warn('Audio initialization failed:', e));
        
        // Set up collision callback for throwables
        this.physics.setThrowableCollisionCallback((throwableBody, velocity) => {
            if (this.audio) {
                this.audio.playWoodSmashWithCooldown(throwableBody, velocity);
            }
        });
        
        this.detachedDoors = [];
        this.detachedClockHands = [];
        this.detachedMirrorPieces = [];
        this.brokenClocks = [];
        this.alarmSoundInterval = null;

        this.wind = { x: 0, y: 0 };
        this.windTimer = 0;
        this.changeWindDirection();

        this.lastTime = performance.now();
        this.flashAlpha = 0;
        this.debugModeDisabled = false; // Flag to temporarily disable debug mode
        this.debugMode = false; // Debug mode starts off by default
        this.fps = 60;
        this.minFPS = 60;  // Track minimum FPS for performance monitoring
        this.frameCount = 0;
        this.fpsTime = 0;

        this.setupInput();
        this.setupIntroInput();
        Input.init(this.canvas, this.camera);
        this.gameLoop();
    }

    /**
     * Load game from a Tiled map file
     * @param {string} mapPath - Path to the Tiled JSON map file
     */
    async loadTiledMap(mapPath) {
        try {
            console.log('loadTiledMap: Starting to load map from', mapPath);
            console.log('loadTiledMap: loadingFromTiled flag =', this.loadingFromTiled);
            
            // Ensure we clear any existing default entities if they somehow exist
            // (shouldn't happen if skipInit worked, but safety check)
            if (this.bed || this.wardrobe || this.alarmClock || this.desk || this.chair || this.chest) {
                console.warn('WARNING: Default entities found before loading Tiled map! Clearing them...');
                this.bed = null;
                this.wardrobe = null;
                this.alarmClock = null;
                this.desk = null;
                this.chair = null;
                this.chest = null;
                this.cupboard = null;
                this.cupboard2 = null;
                // Clear arrays
                this.doors = [];
                this.trees = [];
            }
            
            // Load and parse Tiled map (loadMap already calls parseMap internally)
            const parsedData = await TiledMapLoader.loadMap(mapPath);
            console.log('loadTiledMap: Map loaded successfully, found', parsedData.objects.length, 'objects');

            // Create tilemap from Tiled data (async - loads tileset images)
            this.tilemap = new TileMap(CONFIG.worldWidth, CONFIG.worldHeight, CONFIG.tileSize, parsedData);
            await this.tilemap.initFromTiled(); // Load tileset images and initialize tiles
            const actualWorldWidth = this.tilemap.cols * this.tilemap.tileSize;
            const actualWorldHeight = this.tilemap.rows * this.tilemap.tileSize;
            
            // Camera view excludes the status bar height
            const cameraViewHeight = this.canvas.height - this.hudHeight;
            this.camera = new Camera(actualWorldWidth, actualWorldHeight, this.canvas.width, cameraViewHeight);
            this.physics = new PhysicsSystem(actualWorldWidth, actualWorldHeight);
            this.physics.initDebugRenderer(this.canvas, this.ctx);
            
            this.wallBodies = this.physics.createWallBodies(this.tilemap);

            // Load entities from object layers
            this.loadEntitiesFromTiled(parsedData.objects);
            console.log('loadTiledMap: Entities loaded. bed=', !!this.bed, 'wardrobe=', !!this.wardrobe, 'alarmClock=', !!this.alarmClock);

            // Initialize game systems
            this.particles = new ParticleSystem();
            this.audio = new AudioSystem();
            // Initialize audio (async, but don't await - let it load in background)
            this.audio.init().catch(e => console.warn('Audio initialization failed:', e));
            
            // Set up collision callback for throwables
            this.physics.setThrowableCollisionCallback((throwableBody, velocity) => {
                if (this.audio) {
                    this.audio.playWoodSmashWithCooldown(throwableBody, velocity);
                }
            });

            this.wind = { x: 0, y: 0 };
            this.windTimer = 0;
            this.changeWindDirection();

            this.lastTime = performance.now();
            this.flashAlpha = 0;
            this.debugModeDisabled = false; // Flag to temporarily disable debug mode
            this.debugMode = false; // Debug mode starts off by default
            this.fps = 60;
            this.minFPS = 60;  // Track minimum FPS for performance monitoring
            this.frameCount = 0;
            this.fpsTime = 0;

            this.setupInput();
            this.setupIntroInput();
            Input.init(this.canvas, this.camera);
            this.gameLoop();
        } catch (error) {
            console.error('Failed to load Tiled map:', error);
            throw error;
        }
    }

    /**
     * Create entities from Tiled object layer data
     * @param {Array} objects - Array of object data from Tiled
     */
    loadEntitiesFromTiled(objects) {
        // Note: Tiled uses top-left origin, we use center origin
        // For point objects (width=0, height=0), x,y is already the point position
        // For rectangle objects, x,y is top-left, so we convert to center

        const entityMap = {}; // For parent-child relationships

        console.log(`Loading ${objects.length} entities from Tiled map`);

        for (const obj of objects) {
            // Use type field if available, otherwise use name (normalized to lowercase)
            const type = (obj.type || obj.name || '').toLowerCase();
            console.log(`Loading entity: name="${obj.name}", type="${obj.type}", resolved type="${type}"`);
            // For point objects, x,y is the point itself. For rectangles, convert top-left to center
            const x = obj.point ? obj.x : (obj.x + (obj.width || 0) / 2);
            const y = obj.point ? obj.y : (obj.y + (obj.height || 0) / 2);
            const props = obj.properties;

            let entity = null;

            switch (type) {
                case 'player':
                    this.player = new Player(x, y, this.physics);
                    entity = this.player;
                    break;

                case 'cupboard':
                    const cupboardOutward = props.doorsOpenOutward === true || props.doorsOpenOutward === 'true';
                    entity = new Cupboard(x, y, this.physics, cupboardOutward);
                    if (!this.cupboard) this.cupboard = entity;
                    else if (!this.cupboard2) this.cupboard2 = entity;
                    break;

                case 'cupboard_outward':
                    entity = new Cupboard(x, y, this.physics, true);
                    if (!this.cupboard) this.cupboard = entity;
                    else if (!this.cupboard2) this.cupboard2 = entity;
                    break;

                case 'wardrobe':
                    entity = new Wardrobe(x, y, this.physics);
                    this.wardrobe = entity;
                    break;

                case 'bed':
                    entity = new Bed(x, y, this.physics);
                    this.bed = entity;
                    break;

                case 'sidetable':
                case 'side_table':
                    // SideTable is now part of AlarmClock - create AlarmClock instead
                    entity = new AlarmClock(x, y, this.physics, null);
                    this.alarmClock = entity;
                    if (obj.name) entityMap[obj.name] = entity;
                    break;

                case 'alarmclock':
                case 'alarm_clock':
                    // AlarmClock now includes SideTable - no parent needed
                    entity = new AlarmClock(x, y, this.physics, null);
                    this.alarmClock = entity;
                    break;

                case 'desk':
                    entity = new Desk(x, y, this.physics);
                    this.desk = entity;
                    break;

                case 'chair':
                    entity = new Chair(x, y, this.physics);
                    this.chair = entity;
                    break;

                case 'chest':
                    entity = new Chest(x, y, this.physics);
                    this.chest = entity;
                    break;

                case 'door':
                    const doorWidth = obj.width || 40;
                    const doorHeight = obj.height || 50; // Default to tile height
                    const hingeSide = props.hingeSide || 'left';
                    entity = new Door(x, y, doorWidth, doorHeight, hingeSide, this.physics, 'horizontal');
                    this.doors.push(entity);
                    break;

                case 'door_vertical':
                    const vDoorWidth = obj.width || 50; // Default to tile height for vertical dimension
                    const vDoorHeight = obj.height || 50; // Default to tile height for horizontal dimension
                    const vHingeSide = props.hingeSide || 'left';
                    entity = new Door(x, y, vDoorWidth, vDoorHeight, vHingeSide, this.physics, 'vertical');
                    this.doors.push(entity);
                    break;

                case 'tree':
                    entity = new Tree(x, y, this.physics);
                    this.trees.push(entity);
                    break;

                default:
                    console.warn(`Unknown entity type: ${type} (object: ${obj.name})`);
                    break;
            }

            if (entity && obj.name) {
                entityMap[obj.name] = entity;
            }
        }

        // Ensure arrays are initialized
        if (!this.doors) this.doors = [];
        if (!this.trees) this.trees = [];
        
        // Verify player was created
        if (!this.player) {
            console.error('WARNING: No player entity found in Tiled map!');
        } else {
            console.log(`Player created at (${this.player.x}, ${this.player.y})`);
        }
    }

    setupInput() {
        this.interactKeyPressed = false;
        this.rightMousePressed = false;

        this.getInteractiveObjects = () => {
            const objects = [
                this.cupboard, 
                this.cupboard2, 
                this.wardrobe, 
                this.chest, 
                this.alarmClock, 
                ...this.trees, 
                ...this.doors
            ];
            return objects.filter(obj => obj && !obj.isBroken);
        };

        this.getPickableObjects = () => {
            const objects = [...this.detachedDoors, ...this.detachedClockHands, ...this.detachedMirrorPieces, ...this.brokenClocks];
            return objects.filter(obj => obj && obj.isPickable && !obj.isCarried);
        };

        /**
         * Get entity type string from an entity instance
         * Used by TextureManager to route drawing calls
         */
        this.getEntityType = (obj) => {
            if (!obj) return 'unknown';
            // Use constructor name to determine type
            const className = obj.constructor.name;
            // Map class names to type strings
            const typeMap = {
                'Cupboard': 'cupboard',
                'Wardrobe': 'wardrobe',
                'Chest': 'chest',
                'Door': 'door',
                'Bed': 'bed',
                'Desk': 'desk',
                'Chair': 'chair',
                'Tree': 'tree',
                'AlarmClock': 'alarmClock',
                'Player': 'player',
                'DetachedDoor': 'detachedDoor',
                'DetachedMirrorPiece': 'detachedMirrorPiece',
                'BrokenClock': 'brokenClock',
                'DetachedClockHand': 'detachedClockHand'
            };
            return typeMap[className] || 'unknown';
        };

        window.addEventListener('keydown', (e) => {
            if (!this.audio.initialized) {
                this.audio.init().catch(e => console.warn('Audio initialization failed:', e));
            }
            if ((e.key.toLowerCase() === 'e' || e.key === ' ') && !this.interactKeyPressed) {
                this.interactKeyPressed = true;
                e.preventDefault();
                if (this.player.carryingObject) { this.player.startChargingThrow(); return; }
                const pickables = this.getPickableObjects();
                for (let obj of pickables) {
                    if (obj.isNearPlayer(this.player)) { 
                        this.player.pickupObject(obj); 
                        // If pickup succeeded and key is still held, will auto-start charging in update loop
                        return;
                    }
                }
                const interactives = this.getInteractiveObjects();
                for (let obj of interactives) {
                    if (obj.isNearPlayer(this.player) && obj.canInteract()) { obj.startHold(this.player, this); return; }
                }
            }
            if (e.key.toLowerCase() === 'x' && !e.repeat) {
                // Toggle debug mode disabled flag
                this.debugModeDisabled = !this.debugModeDisabled;
                // If disabled, force debug mode off; if enabled, allow normal debug mode toggle
                if (this.debugModeDisabled) {
                    this.debugMode = false;
                } else {
                    // When re-enabled, debug mode can be toggled normally (but starts off)
                    this.debugMode = false;
                }
            }
        });

        window.addEventListener('keyup', (e) => {
            if (e.key.toLowerCase() === 'e' || e.key === ' ') {
                this.interactKeyPressed = false;
                // Only throw if charging (which means delay has passed)
                if (this.player.isChargingThrow && this.player.carryingObject) {
                    // Use last direction for throw if no mouse target
                    const throwDistance = 200; // Distance to throw in facing direction
                    const throwX = this.player.targetX !== null ? this.player.targetX : 
                                   this.player.x + this.player.lastDirection.x * throwDistance;
                    const throwY = this.player.targetY !== null ? this.player.targetY : 
                                   this.player.y + this.player.lastDirection.y * throwDistance;
                    this.player.throwObject(throwX, throwY, this);
                    return;
                }
                // If released before delay, just carry the object (no throw)
                this.getInteractiveObjects().forEach(obj => { if (obj.isHolding) obj.releaseHold(this); });
            }
        });

        this.canvas.addEventListener('mousedown', (e) => {
            // Check if restart button was clicked (left click only)
            if (e.button === 0) {
                const rect = this.canvas.getBoundingClientRect();
                const mouseX = e.clientX - rect.left;
                const mouseY = e.clientY - rect.top;
                if (this.checkRestartButtonClick(mouseX, mouseY)) {
                    this.restart();
                    return;
                }
            }
            
            if (e.button === 2) {
                e.preventDefault();
                if (!this.audio.initialized) {
                this.audio.init().catch(e => console.warn('Audio initialization failed:', e));
            }
                this.rightMousePressed = true; // Track that right mouse is held
                if (this.player.carryingObject) { this.player.startChargingThrow(); return; }
                const pickables = this.getPickableObjects();
                for (let obj of pickables) {
                    if (obj.isNearPlayer(this.player)) { 
                        this.player.pickupObject(obj); 
                        // If pickup succeeded and right mouse is still held, will auto-start charging in update loop
                        return;
                    }
                }
                const interactives = this.getInteractiveObjects();
                for (let obj of interactives) {
                    if (obj.isNearPlayer(this.player) && obj.canInteract()) { obj.startHold(this.player, this); return; }
                }
            }
        });

        window.addEventListener('mouseup', (e) => {
            if (e.button === 2) {
                if (this.rightMousePressed) {
                this.rightMousePressed = false;
                    // Only throw if charging (which means delay has passed)
                    if (this.player && this.player.isChargingThrow && this.player.carryingObject) {
                        this.player.throwObject(Input.mouseWorldX, Input.mouseWorldY, this);
                    return;
                    }
                    // If released before delay, just carry the object (no throw)
                    this.getInteractiveObjects().forEach(obj => { if (obj.isHolding) obj.releaseHold(this); });
                }
            }
        });
    }

    update(dt) {
        // Don't update game logic if intro screen is showing
        if (this.showIntro) return;
        
        // Safety check: don't update if player hasn't been initialized yet
        if (!this.player) return;
        
        this.physics.update(dt);
        this.player.update(dt, this.tilemap, this);
        // Pass effective debug mode (respects disabled flag)
        const effectiveDebugMode = this.debugMode && !this.debugModeDisabled;
        this.player.updateAggression(dt, effectiveDebugMode);
        
        // Auto-start throw charging if player picked up object and is still holding right-click or E/Space
        // Only starts after throwChargeDelay has passed
        if (this.player.carryingObject && !this.player.isChargingThrow) {
            if (this.rightMousePressed || this.interactKeyPressed) {
                this.player.startChargingThrow();
            }
        }

        // Update all permanent entities
        const entities = [
            this.cupboard, this.cupboard2, this.wardrobe, this.chest, this.alarmClock, 
            this.bed, this.desk, this.chair,
            ...this.trees, ...this.doors
        ];
        entities.forEach(obj => {
            if (!obj) return;
            obj.update(dt);
            if (obj.isHolding && !obj.isBroken) {
                // Pass effective debug mode (respects disabled flag)
                const effectiveDebugMode = this.debugMode && !this.debugModeDisabled;
                obj.updateHold(dt, this.player, effectiveDebugMode);
            }
        });

        // Update all dynamic/detached entities
        const fragments = [...this.detachedDoors, ...this.detachedClockHands, ...this.detachedMirrorPieces, ...this.brokenClocks];
        fragments.forEach(obj => {
            if (obj && obj.isCarried && this.player) obj.updateCarriedPosition(this.player.x, this.player.y);
            if (obj) obj.update(dt, this.tilemap);
        });

        // Alarm sound logic
        if (this.alarmClock) {
            const wasAlarmOn = this.alarmClock.isOn;
        if (this.alarmClock.isOn && !wasAlarmOn && !this.alarmClock.isBroken) {
            this.audio.playAlarmRing();
                if (this.alarmSoundInterval) clearInterval(this.alarmSoundInterval);
                this.alarmSoundInterval = setInterval(() => {
                    if (this.alarmClock.isOn && !this.alarmClock.isBroken) this.audio.playAlarmRing();
                    else { clearInterval(this.alarmSoundInterval); this.alarmSoundInterval = null; }
                }, 500);
            }
            if (!this.alarmClock.isOn && wasAlarmOn && this.alarmSoundInterval) {
                clearInterval(this.alarmSoundInterval);
                this.alarmSoundInterval = null;
            }
        }

        this.windTimer += dt;
        if (this.windTimer >= CONFIG.windChangeInterval) { this.changeWindDirection(); this.windTimer = 0; }
        
        if (this.player) {
            this.camera.follow(this.player.x, this.player.y);
        }
        this.camera.update(dt);
        this.particles.update(dt, this.wind);
        Input.updateWorldCoordinates();

        if (this.flashAlpha > 0) {
            this.flashAlpha -= dt * 5;
            this.flashAlpha = Math.max(0, this.flashAlpha);
        }
    }

    draw() {
        this.ctx.fillStyle = '#000';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        this.camera.apply(this.ctx);
        this.tilemap.draw(this.ctx, this.camera);
        
        // Draw broken/leftover objects first (after tiles, before everything else)
        // These are drawn in fixed order (not Y-sorted) so they always appear behind other objects
        const brokenObjects = [];
        const furniture = [
            ...this.trees,
            this.bed, this.desk, this.chair,
            ...this.doors,
            this.cupboard, this.cupboard2, this.wardrobe, this.chest, this.alarmClock
        ];
        furniture.forEach(obj => {
            if (obj && obj.isBroken) {
                brokenObjects.push(obj);
            }
        });
        
        // Draw broken objects (fixed order, no Y-sorting)
        brokenObjects.forEach(obj => {
            this.textureManager.draw(this.ctx, this.getEntityType(obj), obj, this.camera);
        });
        
        // Y-SORTING: Collect all non-broken drawable entities and sort by Y coordinate
        // Higher Y = further down screen = draw first (behind)
        // Lower Y = further up screen = draw last (in front)
        const drawables = [];
        
        // Add all non-broken static furniture
        furniture.forEach(obj => {
            if (obj && !obj.isBroken) {
                drawables.push({ obj, y: obj.y, type: 'furniture' });
            }
        });
        
        // Add fragments (detached doors, mirror pieces, broken clocks)
        // Separate carried objects from non-carried ones
        const fragments = [...this.detachedDoors, ...this.detachedMirrorPieces, ...this.brokenClocks];
        const carriedObjects = [];
        fragments.forEach(obj => {
            if (obj) {
                if (obj.isCarried) {
                    // Carried objects will be drawn after player (always on top)
                    carriedObjects.push(obj);
                } else {
                    // Non-carried fragments participate in Y-sorting
                    drawables.push({ obj, y: obj.y, type: 'fragment' });
                }
            }
        });
        
        // Add player (if exists)
        if (this.player) {
            drawables.push({ obj: this.player, y: this.player.y, type: 'player' });
        }
        
        // Sort by Y coordinate (ascending: higher Y draws first)
        drawables.sort((a, b) => a.y - b.y);
        
        // Draw in sorted order
        drawables.forEach(({ obj, type }) => {
            if (type === 'player') {
                this.textureManager.drawPlayer(this.ctx, this.player, this.camera);
            } else {
                this.textureManager.draw(this.ctx, this.getEntityType(obj), obj, this.camera);
            }
        });
        
        // Draw carried objects last (always on top of player)
        carriedObjects.forEach(obj => {
            this.textureManager.draw(this.ctx, this.getEntityType(obj), obj, this.camera);
        });
        
        // Particles always on top (they're effects, not objects)
        this.particles.draw(this.ctx, this.camera);
        
        // Draw world-space UI (aggression bars above entities when holding, and above player when charging throw)
        if (this.player) {
            // Draw aggression bar for throw charging
            this.player.drawUI(this.ctx, this.camera);
            // Draw aggression bars for interactive objects
            this.getInteractiveObjects().forEach(obj => obj.drawUI(this.ctx, this.camera, this.player));
        }

        this.camera.restore(this.ctx);
        
        // Only show debug if debug mode is enabled AND not disabled
        if (this.debugMode && !this.debugModeDisabled) {
            this.physics.renderDebug(this.ctx, this.camera);
            this.physics.drawConstraints(this.ctx, this.camera);
        }
        
        this.drawHUD(this.ctx);

        if (this.flashAlpha > 0) {
            this.ctx.fillStyle = `rgba(255, 255, 255, ${this.flashAlpha})`;
            this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        }

        // Draw intro overlay on top of everything
        if (this.showIntro) {
            this.drawIntro(this.ctx);
        }
    }

    gameLoop() {
        const currentTime = performance.now();
        const dt = Math.min((currentTime - this.lastTime) / 1000, 0.1);
        this.lastTime = currentTime;
        this.frameCount++;
        this.fpsTime += dt;
        if (this.fpsTime >= 1.0) {
            this.fps = this.frameCount;
            // Update minimum FPS (track the lowest FPS seen so far)
            if (this.fps < this.minFPS) {
                this.minFPS = this.fps;
            }
            this.frameCount = 0;
            this.fpsTime = 0;
        }
        this.update(dt);
        this.draw();
        requestAnimationFrame(() => this.gameLoop());
    }

    changeWindDirection() {
        const angle = Math.random() * Math.PI * 2;
        this.wind.x = Math.cos(angle) * CONFIG.windStrength;
        this.wind.y = Math.sin(angle) * CONFIG.windStrength;
    }

    setupIntroInput() {
        // Handle any key press to dismiss intro
        this.introKeyHandler = (e) => {
            if (this.showIntro) {
                this.showIntro = false;
                // Remove listeners after dismissing
                window.removeEventListener('keydown', this.introKeyHandler);
                this.canvas.removeEventListener('click', this.introMouseHandler);
                this.canvas.removeEventListener('mousedown', this.introMouseHandler);
            }
        };

        // Handle any mouse click to dismiss intro
        this.introMouseHandler = (e) => {
            if (this.showIntro) {
                this.showIntro = false;
                // Remove listeners after dismissing
                window.removeEventListener('keydown', this.introKeyHandler);
                this.canvas.removeEventListener('click', this.introMouseHandler);
                this.canvas.removeEventListener('mousedown', this.introMouseHandler);
            }
        };

        // Listen for any key press
        window.addEventListener('keydown', this.introKeyHandler);
        
        // Listen for any mouse click (both left and right)
        this.canvas.addEventListener('click', this.introMouseHandler);
        this.canvas.addEventListener('mousedown', this.introMouseHandler);
    }

    drawIntro(ctx) {
        // Semi-transparent dark overlay
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        // Dialog box dimensions (made taller to accommodate new content)
        const dialogWidth = 500;
        const dialogHeight = 400;
        const dialogX = (this.canvas.width - dialogWidth) / 2;
        const dialogY = (this.canvas.height - dialogHeight) / 2;

        // Draw dialog box background
        ctx.fillStyle = '#2a2a2a';
        ctx.fillRect(dialogX, dialogY, dialogWidth, dialogHeight);
        
        // Draw dialog box border
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 3;
        ctx.strokeRect(dialogX, dialogY, dialogWidth, dialogHeight);

        // Draw main heading "Slam Prototype"
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 32px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText('Slam Prototype', this.canvas.width / 2, dialogY + 20);

        // Draw feedback text
        ctx.font = '18px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillStyle = '#ddd';
        const feedbackY = dialogY + 70;
        const lineSpacing = 22;
        ctx.fillText('Please share your feedback (see details below)', this.canvas.width / 2, feedbackY);
        ctx.fillText('Thank you!', this.canvas.width / 2, feedbackY + lineSpacing * 3 + 5);

        // Draw controls section title (with more space after feedback)
        ctx.font = 'bold 24px Arial';
        ctx.fillStyle = '#fff';
        ctx.fillText('Controls', this.canvas.width / 2, dialogY + 185);

        // Draw instructions
        ctx.font = '18px Arial';
        ctx.textAlign = 'left';
        const lineHeight = 30;
        const startY = dialogY + 225;
        const startX = dialogX + 40;

        ctx.fillText('• Left click, arrow keys, and WASD to move', startX, startY);
        ctx.fillText('• Right-click to interact, pick up, and throw', startX, startY + lineHeight);

        // Draw continue prompt
        ctx.font = '16px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = '#aaa';
        ctx.fillText('Press any key or click to continue', this.canvas.width / 2, dialogY + dialogHeight - 20);
    }

    drawHUD(ctx) {
        const hudY = this.canvas.height - this.hudHeight;
        
        // Draw status bar background (non-transparent, solid)
        ctx.fillStyle = '#000000'; // Solid black background
        ctx.fillRect(0, hudY, this.canvas.width, this.hudHeight);
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.strokeRect(0, hudY, this.canvas.width, this.hudHeight);
        
        // Draw restart button in bottom left corner
        this.drawRestartButton(ctx, hudY);
        
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.font = '16px Arial';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'bottom';
        
        if (!this.player) return; // No player, no UI
        
        // Reset alignment for interaction text
        ctx.textAlign = 'right';
        ctx.textBaseline = 'bottom';
        
        let interactionText = '';
        
        if (this.player.carryingObject) {
                interactionText = 'Hold Right-Click/E/Space to charge throw';
        } else {
            let foundPickup = false;
            for (let obj of this.getPickableObjects()) {
                if (obj.isNearPlayer(this.player)) {
                    interactionText = 'E/Space/Right-Click to pick up';
                    foundPickup = true;
                    break;
                }
            }
            if (!foundPickup) {
                for (let obj of this.getInteractiveObjects()) {
                    if (obj.isNearPlayer(this.player) && obj.canInteract()) {
                        if (obj instanceof Tree) interactionText = 'E/Space/Right-Click to shake';
                        else if (obj instanceof Door) interactionText = 'E/Space/Right-Click to open/close';
                        else if (obj instanceof AlarmClock) interactionText = 'E/Space/Right-Click to turn off';
                        else interactionText = 'E/Space/Right-Click to interact';
                        break;
                    }
                }
            }
        }
        if (interactionText) {
            ctx.strokeText(interactionText, this.canvas.width - 20, this.canvas.height - 20);
            ctx.fillText(interactionText, this.canvas.width - 20, this.canvas.height - 20);
        }
        
        // Debug mode: Display debug info (only if not disabled)
        if (this.debugMode && !this.debugModeDisabled) {
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            const debugText = `Min FPS: ${this.minFPS.toFixed(1)}`;
            ctx.strokeText(debugText, 20, 20);
            ctx.fillText(debugText, 20, 20);
            
            // Debug overlay in top right corner
            if (this.player) {
                ctx.textAlign = 'right';
                ctx.textBaseline = 'top';
                const aggressionText = `Aggression: ${this.player.aggression.toFixed(1)}`;
                const lastDamageText = `Last Damage: ${this.lastInteractionAggression.toFixed(1)}`;
                
                // Draw with stroke for visibility
                ctx.strokeText(aggressionText, this.canvas.width - 20, 20);
                ctx.fillText(aggressionText, this.canvas.width - 20, 20);
                ctx.strokeText(lastDamageText, this.canvas.width - 20, 45);
                ctx.fillText(lastDamageText, this.canvas.width - 20, 45);
            }
        }
    }

    drawRestartButton(ctx, hudY) {
        const buttonWidth = 100;
        const buttonHeight = 35;
        const buttonX = 15;
        const buttonY = hudY + (this.hudHeight - buttonHeight) / 2;
        
        // Store button bounds for click detection
        this.restartButtonBounds = {
            x: buttonX,
            y: buttonY,
            width: buttonWidth,
            height: buttonHeight
        };
        
        // Draw button background
        ctx.fillStyle = '#444';
        ctx.fillRect(buttonX, buttonY, buttonWidth, buttonHeight);
        
        // Draw button border
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.strokeRect(buttonX, buttonY, buttonWidth, buttonHeight);
        
        // Draw button text
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('Restart', buttonX + buttonWidth / 2, buttonY + buttonHeight / 2);
    }

    checkRestartButtonClick(mouseX, mouseY) {
        if (!this.restartButtonBounds) return false;
        
        const { x, y, width, height } = this.restartButtonBounds;
        return mouseX >= x && mouseX <= x + width && mouseY >= y && mouseY <= y + height;
    }

    restart() {
        // Reload the page to restart the game
        window.location.reload();
    }
}
